import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import GridOnIcon from '@mui/icons-material/GridOn';

import { EngagementHistoryFormat, UserEngagementHistoryQueryVariables } from 'api';
import { DatePickers } from 'components';
import { View } from '../types';
import { UseDateRange } from 'types/app';
import theme from 'theme';

interface Props {
  view: View;
  variables: UserEngagementHistoryQueryVariables;
  dateRangeOptions: UseDateRange;
  onChange: (variables: UserEngagementHistoryQueryVariables) => void;
  onChangeView: (view: View) => void;
}

const styles = {
  dateRange: {
    display: 'inline-flex',
    background: theme.palette.background.default,
    borderRadius: 20,
  },
} as const;

const NetworkHistoryFilter: FC<Props> = ({
  dateRangeOptions,
  view,
  variables,
  onChange,
  onChangeView,
}) => {
  const { t } = useTranslation();
  const { startDate, setStartDate, endDate, setEndDate, dateRange, setDateRange } =
    dateRangeOptions;

  const isListView = view === View.List;
  const ViewIcon = isListView ? GridOnIcon : ListIcon;
  const viewIconTooltip = isListView ? t('network:gridView') : t('network:listView');

  function handleChangeOrder(event: ChangeEvent<HTMLInputElement>) {
    onChange({
      ...variables,
      format: event.target.value as EngagementHistoryFormat,
    });
  }

  function handleChangeHideTeammates(event: ChangeEvent<HTMLInputElement>) {
    onChange({
      ...variables,
      exclude_internal: event.target.checked,
    });
  }

  function handleChangeView() {
    onChangeView(view === View.List ? View.Grid : View.List);
  }

  return (
    <Grid container alignItems="center" justifyContent="flex-end">
      <Grid xs>
        <Grid container alignItems="center">
          <Grid>
            <Box sx={styles.dateRange}>
              <DatePickers.Range
                includeToday
                dateRange={dateRange}
                startDate={startDate}
                endDate={endDate}
                onSetStart={setStartDate}
                onSetEnd={setEndDate}
                onSetRange={setDateRange}
              />
            </Box>
          </Grid>
          <Box marginLeft={1} marginRight={1} height="auto" alignSelf="stretch" />
          <Grid marginRight={2}>
            <Typography variant="body2">{t('network:order')}</Typography>
          </Grid>
          <Grid>
            <RadioGroup row value={variables.format} onChange={handleChangeOrder}>
              <FormControlLabel
                value={EngagementHistoryFormat.Chronological}
                control={<Radio color="primary" />}
                label={t('common:newest')}
              />
              <FormControlLabel
                value={EngagementHistoryFormat.Aggregate}
                control={<Radio color="primary" />}
                label={t('common:top')}
              />
            </RadioGroup>
          </Grid>
          <Box marginRight={2} height="auto" alignSelf="stretch">
            <Divider orientation="vertical" />
          </Box>
          <Grid>
            <FormControlLabel
              control={
                <Checkbox
                  checked={variables.exclude_internal}
                  onChange={handleChangeHideTeammates}
                />
              }
              label={t('network:hideTeammates')}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Tooltip title={viewIconTooltip}>
          <IconButton onClick={handleChangeView} size="large">
            <ViewIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default NetworkHistoryFilter;
